import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { API_BASE_URL } from '@/utils/env';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';

export default function UserProfileMenu() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { i18n, t } = useTranslation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [userData, setUserData] = useState<{ firstName?: string; lastName?: string; email?: string } | null>(null);
    const [currentLang, setCurrentLang] = useState(i18n.language);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user) return;

            try {
                const { getToken } = await import('@/utils/auth');
                const token = getToken();

                const response = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.user) {
                        setUserData(data.user);
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };

        fetchUserData();
    }, [user]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('authTokens');
        navigate('/');
    };

    const emailPrefix = userData?.email ? (typeof userData.email === 'string' ? userData.email.split('@')[0] : null) : null;
    const userName = userData?.firstName && userData?.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : emailPrefix || 'User';
    const userEmail = userData?.email ?? null;

    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        setCurrentLang(lang);
        // Force direction update immediately for better UX
        document.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        // Close menu
        setShowProfileMenu(false);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-3 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors border border-transparent hover:border-gray-200"
            >
                <div className="flex flex-col items-start hidden sm:flex">
                    <span className="text-sm font-medium text-gray-700">{userName}</span>
                    {userEmail && <span className="text-xs text-gray-400">({userEmail})</span>}
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center border border-gray-200">
                    <User className="w-4 h-4 text-gray-600" />
                </div>
            </button>

            {showProfileMenu && (
                <div className="absolute end-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-4 py-3 border-b border-gray-100 mb-1">
                        <p className="text-sm font-medium text-gray-900">{userName}</p>
                        <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                    </div>

                    {/* Language Selector */}
                    <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">{t('userProfile.language')}</p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => changeLanguage('en')}
                                className={`flex-1 px-2 py-1 text-xs rounded border ${currentLang === 'en' ? 'acc-soft-bg acc-line acc-ink' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                            >
                                English
                            </button>
                            <button
                                onClick={() => changeLanguage('ar')}
                                className={`flex-1 px-2 py-1 text-xs rounded border ${currentLang === 'ar' ? 'acc-soft-bg acc-line acc-ink' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                            >
                                العربية
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            navigate('/profile');
                            setShowProfileMenu(false);
                        }}
                        className="w-full text-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        {t('userProfile.profileSettings')}
                    </button>
                    <button
                        onClick={handleLogout}
                        className="w-full text-start px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                        {t('sidebar.signOut')}
                    </button>
                </div>
            )}
        </div>
    );
}
